import { z } from 'zod';
import { HSN_PATTERN, TSN_PATTERN } from '../constants.js';
import { INSURED_PARTY_TYPES, LEAD_TYPES } from '../enums.js';
import { locationSchema, optionalShortText, shortText, uuid } from './common.js';

/**
 * Questionnaire payloads, one per product.
 *
 * The legacy endpoints took these as JSON blobs stuffed into form fields
 * (`personal_info` with keys `val1`…`val10`) and read them with no checks at
 * all — a missing key was a PHP notice and an empty column. Each field is now
 * named and bounded.
 */

const documentAssetIds = z.array(uuid).max(8).default([]);

// --- KFZ (motor) -----------------------------------------------------------

export const kfzLeadSchema = z.object({
  location: locationSchema,
  documentAssetIds,
  /** Legacy `usage_of_car.mainText` — Gewerblich | Privat. */
  usageType: shortText(60),
  /** Legacy `usage_of_car.subText` — Neu | Wechsel. */
  contractKind: shortText(60),
  hsn: z
    .string()
    .trim()
    .regex(HSN_PATTERN, 'HSN besteht aus 4 Ziffern')
    .optional(),
  tsn: z
    .string()
    .trim()
    .toUpperCase()
    .regex(TSN_PATTERN, 'TSN besteht aus 3 Zeichen')
    .optional(),
  sfClass: optionalShortText(40),
  sfClassComprehensive: optionalShortText(40),
  annualMileageKm: optionalShortText(40),
  driverAge: optionalShortText(40),
  insuranceScope: optionalShortText(80),
  deductible: optionalShortText(60),
  paymentMethod: optionalShortText(60),
  vehicleCount: optionalShortText(20),
  additionalDrivers: optionalShortText(40),
  youngestDriverAge: optionalShortText(40),
});

// --- PHV (private liability) ----------------------------------------------

export const phvLeadSchema = z.object({
  location: locationSchema,
  documentAssetIds,
  insuredParty: shortText(60),
  publicService: optionalShortText(60),
});

// --- GKV (statutory health) ------------------------------------------------

export const gkvLeadSchema = z.object({
  location: locationSchema,
  documentAssetIds,
  professionalGroup: shortText(120),
  grossAnnualSalary: optionalShortText(40),
  currentInsuranceStatus: optionalShortText(80),
});

// --- BSV / BU (occupational disability) ------------------------------------

export const bsvLeadSchema = z.object({
  location: locationSchema,
  documentAssetIds,
  personAge: shortText(20),
  jobTitle: shortText(160),
  employmentType: optionalShortText(80),
  monthlyIncome: optionalShortText(40),
  moneyValidation: optionalShortText(40),
  officeHours: optionalShortText(40),
});

// --- RS (legal expenses) ---------------------------------------------------

/** The app sends these module flags as strings; accept both shapes. */
const coverageFlag = z
  .union([z.boolean(), z.enum(['0', '1', 'true', 'false', 'ja', 'nein', ''])])
  .transform((v) => v === true || v === '1' || v === 'true' || v === 'ja')
  .default(false);

export const rsLeadSchema = z.object({
  location: locationSchema,
  documentAssetIds,
  insuredParty: shortText(60),
  policyholderAge: optionalShortText(20),
  jobStatus: optionalShortText(80),
  residentsCount: optionalShortText(20),
  annualRentalIncome: optionalShortText(40),
  coversPrivate: coverageFlag,
  coversJob: coverageFlag,
  coversTraffic: coverageFlag,
  coversResidence: coverageFlag,
  coversRental: coverageFlag,
});

// --- PKV (private health) --------------------------------------------------

const pkvAdultSchema = z.object({
  policyholderAge: shortText(20),
  professionalGroup: shortText(120),
  grossAnnualSalary: optionalShortText(40),
  aidProvider: optionalShortText(40),
  subsidyRate: optionalShortText(40),
  serviceScope: optionalShortText(40),
  deductible: optionalShortText(40),
});

const pkvChildSchema = z.object({
  age: shortText(20),
});

/**
 * PKV asks a different question set per insured party. A discriminated union
 * makes the invalid combinations unrepresentable, where the legacy code ran
 * three `if/else if` branches and wrote whatever happened to be present.
 */
export const pkvLeadSchema = z
  .object({
    location: locationSchema,
    documentAssetIds,
    insuredParty: z.enum(INSURED_PARTY_TYPES),
    parentCivilStatus: optionalShortText(80),
    parentInsurance: optionalShortText(80),
    serviceScope: optionalShortText(40),
    adults: z.array(pkvAdultSchema).max(6).default([]),
    children: z.array(pkvChildSchema).max(12).default([]),
  })
  .superRefine((value, ctx) => {
    if (value.insuredParty === 'ADULT' && value.adults.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['adults'],
        message: 'Genau eine erwachsene Person erforderlich',
      });
    }
    if (value.insuredParty === 'CHILD' && value.children.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['children'],
        message: 'Mindestens ein Kind erforderlich',
      });
    }
    if (value.insuredParty === 'FAMILY' && value.adults.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['adults'],
        message: 'Mindestens eine erwachsene Person erforderlich',
      });
    }
  });

// --- Dispatch --------------------------------------------------------------

export const leadSchemasByType = {
  KFZ: kfzLeadSchema,
  PHV: phvLeadSchema,
  PKV: pkvLeadSchema,
  GKV: gkvLeadSchema,
  BSV: bsvLeadSchema,
  RS: rsLeadSchema,
} as const;

export const leadTypeParamSchema = z.object({
  type: z.enum(LEAD_TYPES),
});

export const leadListQuerySchema = z.object({
  type: z.enum(LEAD_TYPES).optional(),
});

export type KfzLeadInput = z.infer<typeof kfzLeadSchema>;
export type PhvLeadInput = z.infer<typeof phvLeadSchema>;
export type GkvLeadInput = z.infer<typeof gkvLeadSchema>;
export type BsvLeadInput = z.infer<typeof bsvLeadSchema>;
export type RsLeadInput = z.infer<typeof rsLeadSchema>;
export type PkvLeadInput = z.infer<typeof pkvLeadSchema>;
